/**
 * Shared shapes for the Personalized Action Plan product (2026-08-28).
 *
 * One row in `action_plans` per bill a customer wants a written plan for.
 * The customer uploads documents and answers a few questions, we write
 * the plan in the admin, the row flips to `delivered`. Mirrors the old
 * EOB Review flow (kept in the codebase, no longer sold).
 */

export type ActionPlanStatus = 'draft' | 'submitted' | 'in_review' | 'delivered';

export type PlanDocumentKind = 'bill' | 'eob' | 'other';

export interface PlanDocument {
  kind: PlanDocumentKind;
  /** Object path inside the medical_bills bucket. */
  path: string;
  name: string;
  size?: number;
  type?: string;
}

export interface PlanAttachment {
  path: string;
  name: string;
  size?: number;
  type?: string;
}

export interface ActionPlanIntake {
  /* Read off the bill since 2026-08-28, no longer asked. Kept for older
     rows and for whoever reads the bill in the admin. */
  bill_amount?: number | null;
  provider_name?: string;
  state?: string;
  service_date?: string;
  /* What the bill cannot say. */
  insurance?: string;
  insurance_billed?: string;
  bill_for?: string;
  visit_type?: string;
  estimate_given?: string;
  other_bills?: string;
  bill_status?: string;
  deadline_kind?: string;
  deadline_date?: string;
  itemized_requested?: string;
  contacted?: string;
  contact_notes?: string;
  fa_status?: string;
  household_size?: string;
  income_band?: string;
  income_dropped?: string;
  medicaid_status?: string;
  lump_sum?: string;
  monthly_budget?: string;
  goals?: string[];
  disputed_items?: string;
  notes?: string;
}

export interface ActionPlanRow {
  id: string;
  user_id: string;
  status: ActionPlanStatus;
  documents: PlanDocument[];
  intake: ActionPlanIntake;
  plan_summary: string | null;
  plan_body: string | null;
  attachments: PlanAttachment[];
  /** When the customer sent the case file in. NULL while it is a draft; the 24h clock runs from here. */
  submitted_at: string | null;
  delivered_at: string | null;
  created_at: string;
  updated_at: string;
}

/* Everything below is the intake vocabulary. Values are what gets stored,
   labels are what the customer sees. Keep values stable, they end up in
   the admin and in the plan itself. */

export const INSURANCE_OPTIONS = [
  { value: 'none', label: "I don't have insurance" },
  { value: 'insured_full', label: 'I have insurance but I owe the full amount' },
  { value: 'insured_deductible', label: 'I have insurance and this is my deductible' },
  { value: 'insured_coinsurance', label: 'I have insurance and this is coinsurance or a copay' },
  { value: 'unsure', label: 'Not sure yet' },
] as const;

export const BILL_STATUS_OPTIONS = [
  { value: 'new', label: 'I just received it' },
  { value: 'reminders', label: "I've had reminders or a final notice" },
  { value: 'collections', label: "It's been sent to collections" },
  { value: 'partly_paid', label: "I've already paid part of it" },
] as const;

export const YES_NO_UNSURE = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'unsure', label: 'Not sure' },
] as const;

export const YES_NO = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
] as const;

/* Added 2026-08-28: the answers that decide which route the plan takes,
   none of which are printed on the bill. */

export const BILL_FOR_OPTIONS = [
  { value: 'me', label: 'Me' },
  { value: 'child', label: 'My child' },
  { value: 'spouse', label: 'My spouse or partner' },
  { value: 'deceased', label: 'A relative who has died' },
  { value: 'other', label: 'Someone else' },
] as const;

export const VISIT_TYPE_OPTIONS = [
  { value: 'emergency', label: 'It was an emergency' },
  { value: 'scheduled', label: 'It was scheduled in advance' },
  { value: 'walk_in', label: 'Urgent care or a walk-in' },
  { value: 'unsure', label: 'Not sure' },
] as const;

export const DEADLINE_OPTIONS = [
  { value: 'none', label: 'No' },
  { value: 'due_date', label: 'A due date on a letter' },
  { value: 'collections', label: 'A collections deadline' },
  { value: 'court', label: 'A court date' },
] as const;

export const FA_STATUS_OPTIONS = [
  { value: 'not_mentioned', label: 'No one has mentioned it' },
  { value: 'told', label: "They mentioned it, I haven't applied" },
  { value: 'applied', label: "I applied and I'm waiting" },
  { value: 'denied', label: 'I applied and was denied' },
  { value: 'unsure', label: 'Not sure' },
] as const;

export const MEDICAID_OPTIONS = [
  { value: 'no', label: 'No' },
  { value: 'pending', label: 'Yes, waiting to hear' },
  { value: 'enrolled', label: "Yes, I'm enrolled" },
  { value: 'denied', label: 'Yes, and was denied' },
] as const;

export const LUMP_SUM_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'some', label: 'Some of it' },
  { value: 'no', label: 'No' },
] as const;

export const MONTHLY_BUDGET_OPTIONS = [
  { value: 'nothing', label: 'Nothing right now' },
  { value: 'under_50', label: 'Under $50' },
  { value: '50_100', label: '$50 to $100' },
  { value: '100_250', label: '$100 to $250' },
  { value: '250_500', label: '$250 to $500' },
  { value: 'over_500', label: 'Over $500' },
] as const;

export const CONTACTED_OPTIONS = [
  { value: 'no', label: 'Not yet' },
  { value: 'phone', label: 'Yes, by phone' },
  { value: 'writing', label: 'Yes, in writing' },
  { value: 'both', label: 'Yes, both' },
] as const;

export const HOUSEHOLD_SIZE_OPTIONS = ['1', '2', '3', '4', '5', '6', '7', '8+'] as const;

export const INCOME_BAND_OPTIONS = [
  { value: 'under_30k', label: 'Under $30,000' },
  { value: '30k_60k', label: '$30,000 to $60,000' },
  { value: '60k_100k', label: '$60,000 to $100,000' },
  { value: '100k_150k', label: '$100,000 to $150,000' },
  { value: 'over_150k', label: 'Over $150,000' },
  { value: 'prefer_not', label: 'Prefer not to say' },
] as const;

export const GOAL_OPTIONS = [
  { value: 'lower_total', label: 'Lower the total' },
  { value: 'payment_plan', label: 'Get a payment plan I can afford' },
  { value: 'dispute_charges', label: "Dispute charges I don't recognize" },
  { value: 'stop_collections', label: 'Stop collections' },
  { value: 'financial_assistance', label: 'Find out if I qualify for financial assistance' },
] as const;

export const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME',
  'MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI',
  'SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
] as const;

/** Where a customer's uploads live in the medical_bills bucket. */
export function planDocumentPath(userId: string, fileName: string): string {
  const safe = fileName.replace(/[^\w.-]+/g, '_').slice(0, 120);
  return `action-plans/${userId}/${Date.now()}-${safe}`;
}

export const ACCEPTED_DOC_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp', 'image/heic'];
export const ACCEPTED_DOC_EXTENSIONS = '.pdf,.jpg,.jpeg,.png,.webp,.heic';
export const MAX_DOC_BYTES = 15 * 1024 * 1024;

/** Human label for a stored intake value, falls back to the raw value. */
export function labelFor(
  options: ReadonlyArray<{ value: string; label: string }>,
  value: string | undefined | null,
): string {
  if (!value) return '';
  return options.find((o) => o.value === value)?.label ?? value;
}

/** How many plans the account has paid for that have not been used yet. */
export function plansRemaining(billsLimit: number | null | undefined, used: number): number {
  return Math.max((billsLimit ?? 0) - used, 0);
}
